import React, { useState } from "react";
import { PageHeader, Section } from "@/components/ui-kit";

const SECTIONS = [
  { id: "1", title: "1. Resumen ejecutivo" },
  { id: "2", title: "2. Módulos funcionales del MVP" },
  { id: "3", title: "3. Modelo de datos" },
  { id: "4", title: "4. Reglas de negocio" },
  { id: "5", title: "5. Flujos principales" },
  { id: "6", title: "6. Dashboard y KPIs" },
  { id: "7", title: "7. UX/UI" },
  { id: "8", title: "8. Tecnología recomendada" },
  { id: "9", title: "9. Roadmap por fases" },
  { id: "10", title: "10. Backlog priorizado" },
  { id: "11", title: "11. User stories" },
  { id: "12", title: "12. Criterios de aceptación" },
  { id: "13", title: "13. Riesgos funcionales" },
  { id: "14", title: "14. Wireframes textuales" },
];

export default function Docs() {
  const [active, setActive] = useState("1");

  return (
    <div data-testid="docs-page">
      <PageHeader
        title="Documentación de producto"
        subtitle="Propuesta completa de arquitectura funcional y técnica de la aplicación."
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1">
          <div className="sticky top-20 bg-surface border border-line rounded-sm p-2">
            {SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => {
                  setActive(s.id);
                  document.getElementById(`sec-${s.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                data-testid={`docs-nav-${s.id}`}
                className={`w-full text-left text-sm px-3 py-2 rounded-sm font-body transition-colors ${
                  active === s.id ? "bg-brand/10 text-brand" : "text-ink2 hover:text-ink hover:bg-sidebar"
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </aside>

        <article className="lg:col-span-3 prose-editorial space-y-10 text-ink">
          <Block id="1" title="1. Resumen ejecutivo">
            <p><strong>Tipo de aplicación recomendada:</strong> aplicación web interna tipo <em>dashboard / ERP-lite</em>, mono-empresa, mono-moneda (EUR), mono-idioma (ES), con solo 2 usuarias internas. No es una tienda online ni un ERP genérico.</p>
            <p><strong>Por qué ligera y no un ERP:</strong></p>
            <ul>
              <li>El volumen es pequeño (9 títulos, 2 usuarias).</li>
              <li>No hay RRHH, no hay contabilidad, no hay royalties, no hay transportistas.</li>
              <li>Un ERP introduce complejidad, menús inútiles y curva de aprendizaje que una editorial pequeña no necesita.</li>
              <li>Prioridad absoluta: claridad, trazabilidad del stock y cero descuadres.</li>
            </ul>
            <p><strong>Alcance del MVP:</strong> Catálogo, Clientes, Distribuidores, Almacenes (propio + depósitos), Movimientos de stock, Liquidaciones mensuales, Facturas exportables (PDF/CSV), Devoluciones, Dashboard y Informes. Sin WooCommerce todavía.</p>
          </Block>

          <Block id="2" title="2. Módulos funcionales del MVP">
            <ModuleItem name="Dashboard" desc="KPIs esenciales: stock propio, stock en distribuidor, stock en depósito, ventas mes, ventas canal, liquidaciones pendientes, devoluciones, margen por título." />
            <ModuleItem name="Catálogo de libros" desc="Ficha por título: ISBN, título, autores, ilustración, PVP, coste, IVA 4%, páginas, edad, portada, notas." />
            <ModuleItem name="Clientes y contactos" desc="Librerías, escuelas, ferias, particulares. Con NIF, email, dirección, ciudad." />
            <ModuleItem name="Distribuidores" desc="Con comisión (% sobre PVP) y día de liquidación. Al crear se genera su almacén de depósito automáticamente." />
            <ModuleItem name="Almacenes" desc="3 tipos: propio, depósito distribuidor, depósito en cliente. Permite tener varios sin líos." />
            <ModuleItem name="Stock en depósito" desc="Vista específica que lista todo lo que está físicamente fuera de la editorial pero aún no vendido." />
            <ModuleItem name="Movimientos de stock" desc="Entrada imprenta, envío a depósito, venta directa, venta feria, devolución, deterioro, traslado, liquidación de venta." />
            <ModuleItem name="Pedidos básicos" desc="Se gestionan como líneas de factura. Pedido simplificado = factura en borrador con salida de stock." />
            <ModuleItem name="Liquidaciones mensuales" desc="Por distribuidor y mes. Al confirmar, la app genera todos los movimientos de stock automáticamente (vendidos, devueltos, deteriorados)." />
            <ModuleItem name="Facturas exportables" desc="PDF y CSV. Numeración automática YYYY-NNNN. Con IVA 4% libros por defecto." />
            <ModuleItem name="Devoluciones básicas" desc="Desde distribuidor o cliente. Reintegrables (vuelven al almacén) o no (deterioradas)." />
            <ModuleItem name="Informes" desc="Exportación de stock consolidado a CSV/Excel." />
            <ModuleItem name="Configuración" desc="Gestión de almacenes, datos del usuario." />
          </Block>

          <Block id="3" title="3. Modelo de datos">
            <p>Todas las colecciones usan UUIDs como identificador. Sin ORM, persistencia en MongoDB con Motor (async).</p>
            <Table headers={["Colección", "Campos principales", "Propósito"]} rows={[
              ["books", "id, isbn (unique), title, subtitle, authors, illustrators, pvp, cost, tax_rate, pages, age_range, cover_url, active, notes", "Catálogo de títulos."],
              ["editions", "(MVP: integrado en books)", "En MVP una sola edición por libro. Futuro: soportar reediciones."],
              ["customers", "id, name, type, email, phone, nif, address, city, country, notes", "Librerías, escuelas, ferias, particulares."],
              ["distributors", "id, name, contact_person, email, phone, nif, commission_pct, settlement_day, notes", "Distribuidores. La comisión (55% típica) se aplica sobre PVP en liquidaciones."],
              ["warehouses", "id, name, type (propio|distribuidor|deposito_cliente), location_ref, address, active", "Ubicaciones físicas o lógicas. location_ref apunta a distributor_id o customer_id según tipo."],
              ["stock_movements", "id, date, book_id, type, from_warehouse_id, to_warehouse_id, quantity, unit_price, reference, notes, created_by", "Única fuente de verdad del stock. El stock se calcula sumando/restando deltas por (book, warehouse)."],
              ["consignment_stock", "(computado desde movimientos + warehouses de tipo distribuidor/deposito_cliente)", "Vista derivada, no colección física. Evita descuadres."],
              ["monthly_settlements", "id, distributor_id, period_year, period_month, lines[], status, commission_pct, net_amount, created_at, confirmed_at", "Liquidación mensual. Al confirmar dispara movimientos."],
              ["invoices", "id, number (YYYY-NNNN), date, customer_id|distributor_id, channel, lines[], subtotal, tax, total, status, notes", "Factura con líneas."],
              ["invoice_lines", "(embebidas en invoices.lines)", "book_id, quantity, unit_price, discount_pct, tax_rate."],
              ["returns", "id, date, from_type, from_ref_id, book_id, quantity, reason, reintegrable, movement_id", "Devoluciones. Si reintegrable crea un movimiento."],
              ["users", "id, email (unique), password_hash, name, role", "Máximo 2 usuarias. Seed desde .env."],
              ["audit_logs", "id, user_email, action, entity, entity_id, timestamp, data", "Trazabilidad mínima de operaciones críticas."],
            ]} />
            <p><strong>Relaciones clave:</strong></p>
            <ul>
              <li><code>stock_movements.book_id</code> → <code>books.id</code></li>
              <li><code>stock_movements.from_warehouse_id / to_warehouse_id</code> → <code>warehouses.id</code></li>
              <li><code>warehouses.location_ref</code> → <code>distributors.id</code> o <code>customers.id</code> según tipo</li>
              <li><code>monthly_settlements.distributor_id</code> → <code>distributors.id</code></li>
            </ul>
          </Block>

          <Block id="4" title="4. Reglas de negocio">
            <Rule title="Stock en almacén propio">Sumatorio de deltas de movimientos donde el almacén propio es <code>to</code> (suma) o <code>from</code> (resta). Nunca puede ser negativo en pantalla: si lo fuera, se destaca en rojo y hay que revisar movimientos.</Rule>
            <Rule title="Stock enviado a distribuidor">Cuando se hace <em>envío a depósito</em>, sale del almacén propio y entra en el almacén del distribuidor. Sigue siendo propiedad de la editorial hasta que se liquide.</Rule>
            <Rule title="Stock en depósito (cliente)">Idéntico al distribuidor pero para librerías/clientes con acuerdo de depósito. Unidad no facturada hasta liquidación o venta.</Rule>
            <Rule title="Stock vendido por distribuidor (liquidación)">Al confirmar una liquidación mensual, por cada línea con <em>vendidos</em> se crea un movimiento <code>liquidacion_venta</code> que resta del depósito del distribuidor y se registra como ingreso con comisión aplicada.</Rule>
            <Rule title="Stock devuelto desde distribuidor">En la liquidación, la columna <em>devueltos</em> genera un movimiento <code>devolucion</code> que resta del depósito distribuidor y suma al almacén propio.</Rule>
            <Rule title="Stock deteriorado o promocional">Desaparece del stock mediante movimiento <code>deterioro</code>. No genera ingreso. Queda en histórico para análisis.</Rule>
            <Rule title="Cálculo de stock disponible">stock_disponible(libro) = Σ(propio). stock_total = propio + distribuidor + depósito_cliente.</Rule>
            <Rule title="Prevención de descuadres">(1) Movimientos no editables, solo borrables con trazabilidad. (2) Liquidaciones confirmadas no se pueden borrar. (3) El sistema exige almacén de depósito existente al confirmar una liquidación. (4) Audit log por movimiento.</Rule>
            <Rule title="Trazabilidad por título / ISBN / canal">Cada movimiento lleva ISBN (a través de book_id), canal (inferido del tipo), fecha, usuaria y referencia (factura o liquidación).</Rule>
          </Block>

          <Block id="5" title="5. Flujos principales">
            <Flow title="5.1 Registrar entrada inicial de stock" steps={[
              "Ir a Movimientos → Nuevo movimiento",
              "Tipo: Entrada desde imprenta",
              "Seleccionar libro y almacén destino (tu almacén propio)",
              "Cantidad: p.ej. 500",
              "Guardar → se suma al stock propio",
            ]} />
            <Flow title="5.2 Enviar stock al distribuidor" steps={[
              "Tener creado distribuidor (se genera su almacén de depósito automáticamente)",
              "Movimientos → Nuevo → 'Envío a depósito'",
              "Origen: almacén propio. Destino: depósito del distribuidor",
              "Cantidad y notas",
              "Stock propio baja, depósito distribuidor sube",
            ]} />
            <Flow title="5.3 Registrar stock en depósito (cliente)" steps={[
              "Crear cliente (librería/escuela)",
              "Configuración → Almacenes → Nuevo almacén tipo 'depósito cliente', asociado al cliente",
              "Movimientos → Nuevo 'Envío a depósito' hacia ese almacén",
            ]} />
            <Flow title="5.4 Registrar liquidación mensual" steps={[
              "Liquidaciones → Nueva liquidación",
              "Seleccionar distribuidor, año, mes",
              "Rellenar por libro: vendidos, devueltos, deteriorados, PVP unitario",
              "Guardar como borrador (revisión)",
              "Confirmar → la app genera automáticamente todos los movimientos",
              "Neto editorial = PVP × (1 − comisión%)",
            ]} />
            <Flow title="5.5 Convertir depósito en venta real" steps={[
              "Este paso ocurre como parte del flujo 5.4",
              "Alternativamente, Movimientos → tipo 'liquidacion_venta' manual desde un depósito",
            ]} />
            <Flow title="5.6 Registrar devolución desde distribuidor" steps={[
              "Vía liquidación (columna devueltos) — recomendado",
              "O bien: Devoluciones → Nueva devolución, origen distribuidor, libro, cantidad, motivo, reintegrable",
              "Si reintegrable: destino almacén propio → crea movimiento",
            ]} />
            <Flow title="5.7 Registrar ventas en feria" steps={[
              "Opción A: Movimientos → tipo 'Venta en feria' desde almacén propio",
              "Opción B: Facturas → Nueva, canal 'feria', con salida de stock automática",
            ]} />
            <Flow title="5.8 Crear factura exportable" steps={[
              "Facturas → Nueva factura",
              "Seleccionar canal, cliente o distribuidor, almacén de salida (opcional)",
              "Añadir líneas: al elegir libro se autocompleta PVP e IVA",
              "Emitir → número YYYY-NNNN",
              "Exportar PDF o CSV desde la fila",
            ]} />
            <Flow title="5.9 Consultar stock consolidado" steps={[
              "Almacén → vista maestra con propio/distribuidor/depósito/total",
              "Filtrar por título o ISBN",
              "Exportar a Excel/CSV",
            ]} />
          </Block>

          <Block id="6" title="6. Dashboard y KPIs">
            <ul>
              <li><strong>Stock en almacén propio</strong> (unidades totales).</li>
              <li><strong>Stock en distribuidor</strong> (unidades en depósito).</li>
              <li><strong>Stock en depósito en cliente</strong> (unidades).</li>
              <li><strong>Ventas por mes</strong> (6 meses, gráfico de barras).</li>
              <li><strong>Ventas por canal</strong> (directo, feria, distribuidor - gráfico de anillo).</li>
              <li><strong>Liquidaciones pendientes</strong> (número de borradores).</li>
              <li><strong>Devoluciones</strong> (histórico).</li>
              <li><strong>Coste básico y margen por título</strong> (tabla ordenable).</li>
              <li><strong>Títulos activos</strong> (activos/total).</li>
            </ul>
          </Block>

          <Block id="7" title="7. UX/UI">
            <ul>
              <li>Sidebar fija (w-64) con 13 entradas + logout.</li>
              <li>Topbar con búsqueda global (título/ISBN/autor) + indicadores <code>ES · EUR · IVA 4%</code>.</li>
              <li>Paleta cálida editorial: crema (#FAF9F5), naranja quemado (#C65D3B), oliva (#7D8C5A), ámbar (#D49A4D).</li>
              <li>Tipografía: Fraunces (títulos), Work Sans (cuerpo), IBM Plex Mono (ISBN y cifras).</li>
              <li>Fichas limpias, tablas densas pero legibles, sin rayado cebra.</li>
              <li>Diálogos modales para formularios cortos, evita cambiar de pantalla.</li>
              <li>Todos los KPIs en 1 vista. Entre pantalla y acción: máximo 2 clics.</li>
              <li>Pensado para 2 usuarias no técnicas: textos en español, mensajes amables, confirmaciones claras en operaciones destructivas.</li>
            </ul>
          </Block>

          <Block id="8" title="8. Tecnología recomendada">
            <ul>
              <li><strong>Frontend:</strong> React 19 + React Router 7 + Tailwind + Shadcn/UI + Recharts. CRA con Craco. JS/JSX (sin TS) para simplicidad de mantenimiento.</li>
              <li><strong>Backend:</strong> FastAPI (Python) + Motor (Mongo async) + PyJWT + bcrypt. Endpoints bajo <code>/api</code>.</li>
              <li><strong>Base de datos:</strong> MongoDB. Ideal para esquemas flexibles y documentos denormalizados (líneas dentro de factura).</li>
              <li><strong>Autenticación:</strong> JWT con cookie httpOnly + fallback Bearer. 2 usuarias internas, seed desde <code>.env</code>.</li>
              <li><strong>Exportaciones:</strong> ReportLab (PDF), openpyxl (Excel), <code>csv</code> stdlib.</li>
              <li><strong>Integración WooCommerce futura:</strong> cliente REST (WooCommerce API v3) + job periódico que sincronice stock + webhook de pedido → movimiento de stock. Usar <code>api.ts</code> capa aparte.</li>
              <li><strong>Infraestructura:</strong> 1 contenedor web (front+back), 1 Mongo. Backups diarios.</li>
            </ul>
          </Block>

          <Block id="9" title="9. Roadmap por fases">
            <Phase label="Fase 1 – MVP operativo (4-6 semanas)" items={[
              "Catálogo, Clientes, Distribuidores, Almacenes",
              "Movimientos de stock (todos los tipos)",
              "Liquidaciones mensuales con confirmación",
              "Facturas PDF/CSV",
              "Dashboard básico + exportación stock",
              "Login JWT y usuario admin seed",
            ]} />
            <Phase label="Fase 2 – Mejoras y automatizaciones (4 semanas)" items={[
              "Importador CSV/Excel desde el Excel actual",
              "Pedidos formales (con estado: borrador/enviado/entregado)",
              "Filtros avanzados y búsqueda global",
              "Plantillas de factura personalizables",
              "Alertas de stock bajo",
            ]} />
            <Phase label="Fase 3 – Integración WooCommerce (6 semanas)" items={[
              "Sincronización de catálogo (título, ISBN, PVP, portada)",
              "Webhook de pedido → movimiento de venta directa",
              "Sincronización bidireccional de stock propio",
              "Pasarela para evitar oversell (reserva temporal)",
            ]} />
            <Phase label="Fase 4 – Analítica y avanzadas (6 semanas)" items={[
              "Rentabilidad real por título (con costes indirectos)",
              "Previsión de demanda simple",
              "Exportación contable (SII / Factura-e)",
              "Multi-editorial / multi-marca si se necesitara",
            ]} />
          </Block>

          <Block id="10" title="10. Backlog priorizado">
            <h4 className="font-heading text-lg">Imprescindible (P0)</h4>
            <ul>
              <li>CRUD libros con ISBN único y PVP/coste</li>
              <li>CRUD clientes y distribuidores</li>
              <li>Almacenes (propio, distribuidor, depósito cliente)</li>
              <li>Movimientos (8 tipos) con cálculo de stock</li>
              <li>Liquidaciones mensuales con confirmación</li>
              <li>Facturas PDF + CSV</li>
              <li>Dashboard con KPIs de stock y liquidaciones</li>
              <li>Login JWT</li>
            </ul>
            <h4 className="font-heading text-lg mt-4">Importante (P1)</h4>
            <ul>
              <li>Importador de Excel existente</li>
              <li>Búsqueda global</li>
              <li>Exportación de stock a Excel/CSV</li>
              <li>Devoluciones con reintegro</li>
              <li>Alertas de stock bajo</li>
            </ul>
            <h4 className="font-heading text-lg mt-4">Opcional (P2)</h4>
            <ul>
              <li>WooCommerce</li>
              <li>Plantillas de factura</li>
              <li>Informes de rentabilidad avanzada</li>
              <li>Multi-moneda</li>
            </ul>
          </Block>

          <Block id="11" title="11. User stories">
            <Story
              role="Como editora"
              want="quiero registrar una entrada inicial de 500 ejemplares tras imprenta"
              benefit="para empezar a vender sabiendo exactamente cuánto tengo."
            />
            <Story
              role="Como editora"
              want="quiero enviar 200 ejemplares al distribuidor"
              benefit="y que queden como depósito, no como venta, hasta que él los liquide."
            />
            <Story
              role="Como editora"
              want="quiero ver en un panel, de un vistazo, cuánto stock tengo en mi almacén, en distribuidor y en depósito"
              benefit="para tomar decisiones rápidas sobre reimpresión o envíos."
            />
            <Story
              role="Como editora"
              want="quiero introducir la liquidación mensual del distribuidor una vez al mes"
              benefit="y que la app genere automáticamente los movimientos y me calcule el neto a cobrar."
            />
            <Story
              role="Como editora"
              want="quiero registrar ventas de feria en 3 clics"
              benefit="porque al acabar el evento no quiero perder tiempo."
            />
            <Story
              role="Como editora"
              want="quiero generar una factura PDF con IVA 4% del libro"
              benefit="para mandarla a la librería por email."
            />
            <Story
              role="Como editora"
              want="quiero que si me equivoco en un movimiento pueda borrarlo con confirmación"
              benefit="y que quede trazado en el log."
            />
            <Story
              role="Como editora"
              want="quiero buscar por título, ISBN o autor desde cualquier parte"
              benefit="para encontrar info en segundos."
            />
          </Block>

          <Block id="12" title="12. Criterios de aceptación">
            <AC title="Movimientos de stock">
              <li>Todo movimiento válido tiene tipo, libro, cantidad &gt; 0 y fecha.</li>
              <li>Entrada impresión solo tiene destino; venta/deterioro solo origen; envío/traslado tiene ambos.</li>
              <li>El stock consolidado se recalcula al instante tras crear/borrar movimiento.</li>
              <li>No se puede registrar un movimiento con cantidad ≤ 0.</li>
              <li>Cada movimiento queda en el audit log.</li>
            </AC>
            <AC title="Depósito">
              <li>Todo libro con stock &gt; 0 en un almacén tipo distribuidor o depósito_cliente aparece en la pantalla de Depósito.</li>
              <li>La suma de unidades depósito + propio + vendidas ≤ total impreso (control de consistencia).</li>
              <li>No se puede crear un almacén tipo distribuidor sin asociarlo a un distribuidor existente (el sistema lo genera automáticamente al crear el distribuidor).</li>
            </AC>
            <AC title="Liquidaciones mensuales">
              <li>Solo una liquidación por (distribuidor, año, mes) en estado confirmada.</li>
              <li>Al confirmar: se generan movimientos por cada línea con <em>vendidos</em>, <em>devueltos</em>, <em>deteriorados</em>.</li>
              <li>Una liquidación confirmada no se puede borrar.</li>
              <li>Se muestra el neto editorial calculado = PVP_total × (1 − comisión).</li>
            </AC>
            <AC title="Devoluciones">
              <li>Si <em>reintegrable = sí</em>, se genera un movimiento de devolución al almacén propio indicado.</li>
              <li>Si <em>reintegrable = no</em>, solo queda registrada pero no modifica stock propio.</li>
              <li>Siempre se registra el origen (distribuidor o cliente) y el motivo opcional.</li>
            </AC>
            <AC title="Exportación de facturas">
              <li>Toda factura emitida tiene número único <code>YYYY-NNNN</code>.</li>
              <li>El PDF incluye: número, fecha, cliente, líneas, subtotal, IVA, total.</li>
              <li>El CSV es separador <code>;</code> compatible con Excel España.</li>
              <li>Si se marca "Generar movimiento", se crea un movimiento de salida automáticamente.</li>
            </AC>
          </Block>

          <Block id="13" title="13. Riesgos funcionales">
            <Risk title="Duplicar movimientos" mitigation="Cada movimiento muestra fecha, tipo y referencia. Se sugiere referencia única (número de factura o liquidación). Audit log." />
            <Risk title="Descuadres de stock" mitigation="Única fuente de verdad: la colección movements. No hay campo 'stock actual' almacenado. Se recalcula siempre, evitando desync." />
            <Risk title="Liquidar stock incorrectamente" mitigation="Estado borrador antes de confirmar. Al confirmar: aviso fuerte. Liquidación confirmada no editable ni borrable." />
            <Risk title="Confundir depósito con venta real" mitigation="Tipos de movimiento explícitos: envío_depósito ≠ venta_directa. El depósito NO genera ingreso hasta liquidación." />
            <Risk title="Devoluciones mal imputadas" mitigation="Toda devolución debe indicar origen (distribuidor/cliente) y si es reintegrable. El sistema crea el movimiento correcto automáticamente." />
            <Risk title="Pérdida de datos" mitigation="Backup diario MongoDB. Exportación periódica a Excel desde Informes." />
            <Risk title="Usuario sin formación técnica" mitigation="UI simple, mensajes en castellano, confirmaciones claras, tooltips." />
          </Block>

          <Block id="14" title="14. Wireframes textuales">
            <Wire title="Dashboard" content={`┌──────────────────────────────────────────────────────────────┐
│ [Buscar título, ISBN, autor…]              ES · EUR · IVA 4% │
├──────────────────────────────────────────────────────────────┤
│  PROPIO 3.450   DISTRIBUIDOR 1.280   DEPÓSITO 340   LIQ. 2   │
├──────────────────────────────────────────────────────────────┤
│  ◆ Ventas por mes (6m) ─ gráfico barras  │ Ventas canal ●●○  │
│                                          │                    │
├──────────────────────────────────────────┴────────────────────┤
│  Margen por título    ISBN · Título · Coste · PVP · Margen    │
│                       978-…  El osito      4,00   14,00  10€  │
└──────────────────────────────────────────────────────────────┘`} />

            <Wire title="Ficha de libro (modal)" content={`┌─ Nuevo libro ───────────────────────────────────┐
│  ISBN *                Edad recomendada            │
│  ┌────────────┐        ┌────────────┐              │
│  Título *                                          │
│  ┌──────────────────────────────────────┐          │
│  Autores/as             Ilustración                │
│  PVP (€)   Coste (€)   IVA %   Páginas             │
│  Portada URL                                       │
│  Notas                                             │
│                              [Cancelar] [Guardar]  │
└────────────────────────────────────────────────────┘`} />

            <Wire title="Stock (Almacén)" content={`Almacén & Stock                    [CSV] [Excel]
[Filtrar por título o ISBN…]
─────────────────────────────────────────────────
ISBN         Título         PVP   Propio Dist. Dep. Total
978-…        El osito       14€   450    120    30   600
978-…        Luna y el mar  16€   220     80     0   300
…`} />

            <Wire title="Depósito" content={`Stock en Depósito
────────────────────────────────────────
Depósito Distribuidor "Azeta"   1.280 uds · 18.150€
  978-…  El osito       120   14,00 €
  978-…  Luna y el mar   80   16,00 €

Depósito cliente "Librería Central"  45 uds · 630€
  978-…  El osito        45   14,00 €`} />

            <Wire title="Liquidación mensual" content={`Nueva liquidación
Distribuidor: [Azeta ▾]   Año: 2026   Mes: [Febrero ▾]

Título          Vendidos  Devueltos  Deteriorados  PVP    Total
El osito           40          5           0       14,00  560,00
Luna y el mar      22          2           1       16,00  352,00
─────────────────────────────────────────────────────────────
                 62         7           1               912,00
[Cancelar] [Guardar borrador] → Confirmar (irreversible)`} />

            <Wire title="Facturas" content={`Facturas exportables           [Nueva factura]
Número   Fecha       Cliente         Canal         Total
2026-0001 12 feb     Lib. Central    directo      156,00 €  [PDF] [CSV]
2026-0002 18 feb     Azeta           distribuidor 950,00 €  [PDF] [CSV]
…`} />
          </Block>
        </article>
      </div>
    </div>
  );
}

function Block({ id, title, children }) {
  return (
    <section id={`sec-${id}`} className="scroll-mt-24">
      <div className="bg-surface border border-line rounded-sm p-6 md:p-8">
        <h2 className="font-heading text-2xl md:text-3xl text-ink tracking-tight mb-4 border-b border-line pb-3">{title}</h2>
        <div className="space-y-3 text-sm text-ink leading-relaxed docs-body">{children}</div>
      </div>
    </section>
  );
}

function ModuleItem({ name, desc }) {
  return (
    <div className="flex gap-3 py-2 border-b border-line/60 last:border-0">
      <div className="w-1 bg-brand shrink-0 rounded-full" />
      <div>
        <div className="font-medium text-ink">{name}</div>
        <div className="text-ink2 text-sm">{desc}</div>
      </div>
    </div>
  );
}
function Rule({ title, children }) {
  return (
    <div className="border-l-2 border-olive pl-4 py-1">
      <div className="font-heading text-base text-ink">{title}</div>
      <div className="text-ink2 text-sm">{children}</div>
    </div>
  );
}
function Flow({ title, steps }) {
  return (
    <div className="bg-sidebar/40 border border-line rounded-sm p-4 mt-3">
      <div className="font-heading text-base text-ink mb-2">{title}</div>
      <ol className="list-decimal pl-5 space-y-1 text-sm text-ink2">{steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
    </div>
  );
}
function Phase({ label, items }) {
  return (
    <div className="mt-4 border-l-2 border-brand pl-4">
      <div className="font-heading text-lg text-ink">{label}</div>
      <ul className="list-disc pl-5 mt-1 space-y-1 text-sm text-ink2">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
    </div>
  );
}
function Story({ role, want, benefit }) {
  return (
    <div className="bg-surface border border-line rounded-sm p-4 mt-3">
      <div className="text-ink text-sm"><strong>{role}</strong>, {want},</div>
      <div className="text-ink2 text-sm italic mt-1">→ {benefit}</div>
    </div>
  );
}
function AC({ title, children }) {
  return (
    <div className="mt-3">
      <div className="font-heading text-base text-ink">{title}</div>
      <ul className="list-disc pl-5 text-sm text-ink2 mt-1 space-y-1">{children}</ul>
    </div>
  );
}
function Risk({ title, mitigation }) {
  return (
    <div className="mt-3 flex gap-3">
      <div className="w-2 h-2 rounded-full bg-crimson mt-2 shrink-0" />
      <div>
        <div className="font-medium text-ink">{title}</div>
        <div className="text-ink2 text-sm"><strong>Mitigación: </strong>{mitigation}</div>
      </div>
    </div>
  );
}
function Wire({ title, content }) {
  return (
    <div className="mt-4">
      <div className="font-heading text-lg text-ink mb-2">{title}</div>
      <pre className="text-xs font-mono bg-sidebar/50 border border-line rounded-sm p-4 overflow-x-auto whitespace-pre leading-relaxed text-ink2">{content}</pre>
    </div>
  );
}
function Table({ headers, rows }) {
  return (
    <div className="overflow-x-auto border border-line rounded-sm my-3">
      <table className="w-full text-xs">
        <thead><tr className="bg-sidebar/60 text-[11px] uppercase tracking-[0.1em] text-ink2">
          {headers.map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-line align-top">
              {r.map((c, j) => <td key={j} className={`px-3 py-2 ${j === 0 ? "font-mono text-ink" : "text-ink2"}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
